import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PurchaseBillService } from '../../../../Services/PurchaseBill/purchase-bill.service';

@Component({
  selector: 'app-purchase-bill-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './purchase-bill-report.component.html',
  styleUrl: './purchase-bill-report.component.css'
})
export class PurchaseBillReportComponent implements OnInit {
  // Data Containers
  allBills: any[] = [];             // Master list from API
  filteredBills: any[] = [];        // After search
  paginatedBills: any[] = [];       // Visible on current page

  searchTerm: string = '';
  isLoading: boolean = false;
  totalGrandSum: number = 0;

  // Pagination State
  currentPage: number = 1;
  pageSize: number = 10;
  lastPage: number = 1;
  totalItems: number = 0;
  fromDate: string = '';
  toDate: string = '';


  constructor(private purchaseBillService: PurchaseBillService) { }

  ngOnInit() {
    this.loadInitialData();
  }

  loadInitialData() {
    this.isLoading = true;
    // Fetch all records once
    this.purchaseBillService.getPurchaseBills().subscribe({
      next: (res: any) => {
        // Reverse to show newest first
        this.allBills = res.data ? res.data.reverse() : [];
        this.applyFilter(); // Initial display
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.isLoading = false;
      }
    });
  }

  // Frontend Search Logic
  onSearch() {
    this.currentPage = 1; // Reset to page 1 when searching
    this.applyFilter();
  }

  applyFilter() {
    const search = this.searchTerm?.toLowerCase().trim();

    this.filteredBills = this.allBills.filter(bill => {
      // 1. Search Filter
      const matchesSearch =
        !search ||
        bill.id?.toString().includes(search) ||
        bill.amount?.toString().includes(search) ||
        bill.bill_date?.toLowerCase().includes(search);

      // 2. Date Range Filter
      if (!this.fromDate && !this.toDate) return matchesSearch;

      // Helper function to get a local YYYY-MM-DD string
      const toDateString = (dateInput: any) => {
        if (!dateInput) return null;
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return null;

        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const billDateStr = toDateString(bill.bill_date);
      const fromStr = toDateString(this.fromDate);
      const toStr = toDateString(this.toDate);

      const matchesDate =
        (!fromStr || (billDateStr && billDateStr >= fromStr)) &&
        (!toStr || (billDateStr && billDateStr <= toStr));

      return matchesSearch && matchesDate;
    });

    // Update pagination and totals
    this.totalItems = this.filteredBills.length;
    this.lastPage = Math.ceil(this.totalItems / this.pageSize) || 1;
    this.calculateGrandTotal();
    this.updatePagination();
  }

  exportZip() {
    const payload = {
      from_date: this.fromDate,
      to_date: this.toDate
    };

    this.purchaseBillService.downloadPurchaseBillsZip(payload)
      .subscribe((blob: Blob) => {
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `purchase-bills-${this.fromDate || 'all'}-to-${this.toDate || 'all'}.zip`;
        a.click();

        window.URL.revokeObjectURL(url);
      });
  }




  // Slice data for current page
  updatePagination() {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedBills = this.filteredBills.slice(startIndex, endIndex);
  }

  calculateGrandTotal() {
    this.totalGrandSum = this.filteredBills.reduce((acc, curr) =>
      acc + parseFloat(curr.amount || 0), 0
    );
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.lastPage) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  viewDocument(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  deleteRecord(id: any) {
    if (confirm('Are you sure you want to delete this purchase bill?')) {
      this.purchaseBillService.deletePurchaseBills(id).subscribe(() => {
        // Remove locally to avoid full API reload
        this.allBills = this.allBills.filter(b => b.id !== id);
        this.applyFilter();
      });
    }
  }
}